import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { getTokenMetadata } from '../../utils/sso';
import { getImpersonator, finishImpersonation } from '../../utils/impersonation';
import { Styling } from '../../constants';
import { IAppState } from '../../state/reducers';
import { selectUserRole } from '../../state/user/userOrgRole';
import {
  Banner, BannerText, RoleTag, EndButton,
} from './styles';
import { IStateProps } from './interfaces';

/**
 * Persistent reminder that the session is not the admin's own. The identities are read from
 * the token rather than redux so they are correct on first paint, before any store hydration.
 *
 * The role cannot come from the token - it is not a claim - so it comes from redux, which
 * means it arrives a beat later than the rest of the banner. Rendered only once present
 * rather than as a placeholder, so the banner never shows a role that is about to change.
 */
const ImpersonationBanner: React.FC<IStateProps> = ({ roleLabel }) => {
  const [ending, setEnding] = useState(false);
  const impersonator = getImpersonator();

  // The banner is a sibling of the app content inside #root, so a full-height layout
  // would otherwise total viewport + banner and overflow by exactly this much. Publishing
  // the height lets the content shrink to match instead of growing a scrollbar.
  useEffect(() => {
    if (!impersonator) return undefined;

    const root = document.documentElement;
    root.style.setProperty('--impersonation-banner-height', `${Styling.IMPERSONATION_BANNER_HEIGHT}px`);

    return () => { root.style.removeProperty('--impersonation-banner-height'); };
  }, [impersonator]);

  if (!impersonator) return null;

  const target = getTokenMetadata()?.email ?? 'unknown user';

  return (
    <Banner data-auto="ImpersonationBanner">
      <BannerText>
        Viewing as <strong>{target}</strong>
        {roleLabel && (
          <RoleTag data-auto="ImpersonationRole">{roleLabel}</RoleTag>
        )}
        {' '}— you are signed in as <strong>{impersonator}</strong>
      </BannerText>
      <EndButton
        type="button"
        disabled={ending}
        onClick={() => {
          setEnding(true);
          finishImpersonation().catch(() => setEnding(false));
        }}
        data-auto="EndImpersonationBtn"
      >
        {ending ? 'Ending…' : 'End impersonation'}
      </EndButton>
    </Banner>
  );
};

const mapStateToProps = (state: IAppState): IStateProps => {
  // These are the impersonated user's roles: the token swap is followed by a reload, so
  // everything in the store belongs to the target rather than the admin.
  //
  // Every distinct role name, not userVaultRoles[0]. A user with roles in several orgs has
  // no single "current" one here, and naming just the first would silently misdescribe the
  // session in exactly the case an admin is most likely to be investigating.
  //
  // Joined to a string here rather than passed as an array: connect compares props
  // shallowly, so a freshly built array differs by reference on every store update and
  // would re-render the banner on each one. A string compares by value, so this needs no
  // memoisation and no selector factory.
  const roleLabel = Array.from(new Set(
    (selectUserRole(state)?.userVaultRoles ?? [])
      .map((r) => r.roleName)
      .filter(Boolean),
  )).join(', ');

  return { roleLabel };
};

export default connect(mapStateToProps)(ImpersonationBanner);
